import { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { GetVideoPlayback, VideoPlaybackDecision } from "../../../../Api/VideoPlayback";
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";

export type VideoPlaybackState =
    | { status: "loading"; decision?: never; error?: never }
    | { status: "ready"; decision: VideoPlaybackDecision; error?: never }
    | { status: "error"; decision?: never; error: string };

export default function useVideoPlayback(videoId: number | undefined) {
    const auth = useContext(UserAuthenticationContext);
    const authRef = useRef(auth);
    authRef.current = auth;
    const [attempt, setAttempt] = useState(0);
    const hasSession = auth.doesAuthTokenExist();
    const subscription = auth.userData?.subscription;
    // This is only invalidation, never an entitlement decision.
    const scope = JSON.stringify([
        videoId, auth.authInitializing, auth.authSessionVersion, auth.userData?.id,
        hasSession, auth.userData?.admin_role_id, subscription?.status,
        subscription?.plan_id, subscription?.plan_key, subscription?.is_entitled, attempt,
    ]);
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const [result, setResult] = useState<{ scope: string; state: VideoPlaybackState } | null>(null);
    const retry = useCallback(() => setAttempt(value => value + 1), []);

    useEffect(() => {
        if (!videoId || !Number.isSafeInteger(videoId) || videoId <= 0 || authRef.current.authInitializing) return;
        const controller = new AbortController();
        const isCurrent = () => !controller.signal.aborted && scopeRef.current === scope;
        setResult({ scope, state: { status: "loading" } });
        void (async () => {
            try {
                const currentAuth = authRef.current;
                const expectsSession = currentAuth.doesAuthTokenExist() || !!currentAuth.userData?.id;
                let token = expectsSession ? await currentAuth.getValidAccessToken() : null;
                if (!isCurrent()) return;
                if (expectsSession && !token) throw new Error("session_unavailable");
                let decision: VideoPlaybackDecision;
                try {
                    decision = await GetVideoPlayback(videoId, token, controller.signal);
                } catch (error) {
                    if (!isCurrent()) return;
                    if (!token || !axios.isAxiosError(error) || error.response?.status !== 401) throw error;
                    const refreshed = await currentAuth.refreshTokens();
                    if (!isCurrent()) return;
                    token = refreshed ? await currentAuth.getValidAccessToken() : null;
                    if (!isCurrent()) return;
                    if (!token) throw new Error("session_unavailable");
                    decision = await GetVideoPlayback(videoId, token, controller.signal);
                }
                if (isCurrent()) setResult({ scope, state: { status: "ready", decision } });
            } catch (error) {
                if (!isCurrent()) return;
                const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                const message = status === 404
                    ? "This video is currently unavailable."
                    : status === 401 || (error instanceof Error && error.message === "session_unavailable")
                        ? "We could not verify your session. Try again or sign in again."
                        : "We could not check video access. Please try again.";
                setResult({ scope, state: { status: "error", error: message } });
            }
        })();
        return () => controller.abort();
    }, [scope, videoId]);

    // A changed video/session invalidates a grant during render, before effects run.
    const state: VideoPlaybackState = result?.scope === scope ? result.state : { status: "loading" };
    return { state, retry, scope };
}
